import { supabase } from '@/integrations/supabase/client';
import type { SystemVersion, ChangelogEntry } from '@/types/changelog';

/**
 * Fetches the current system version from the database
 */
export async function getCurrentVersion(): Promise<SystemVersion | null> {
  try {
    const { data, error } = await supabase
      .from('system_version')
      .select('*')
      .single();
    
    if (error) {
      console.error('Failed to fetch current version:', error);
      return null;
    }
    
    return data;
  } catch (err) {
    console.error('Unexpected error fetching version:', err);
    return null;
  }
}

/**
 * Fetches paginated changelog entries, ordered by deployment date (newest first)
 */
export async function getChangelogs(limit = 20, offset = 0): Promise<ChangelogEntry[]> {
  try {
    const { data, error } = await supabase
      .from('system_changelogs')
      .select('*')
      .order('deployed_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      console.error('Failed to fetch changelogs:', error);
      return [];
    }
    
    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching changelogs:', err);
    return [];
  }
}

/**
 * Fetches a specific changelog entry by version number
 */
export async function getChangelogByVersion(version: string): Promise<ChangelogEntry | null> {
  try {
    const { data, error } = await supabase
      .from('system_changelogs')
      .select('*')
      .eq('version', version)
      .single();
    
    if (error) {
      console.error(`Failed to fetch changelog for version ${version}:`, error);
      return null;
    }
    
    return data;
  } catch (err) {
    console.error('Unexpected error fetching changelog by version:', err);
    return null;
  }
}

/**
 * Checks if a version was deployed within the last 48 hours
 */
export async function isNewVersion(version: string): Promise<boolean> {
  try {
    const { data: deployedAt, error } = await supabase
      .from('system_changelogs')
      .select('deployed_at')
      .eq('version', version)
      .single();
    
    if (error || !deployedAt) {
      return false;
    }
    
    const hoursSinceDeployment = 
      (Date.now() - new Date(deployedAt.deployed_at).getTime()) / (1000 * 60 * 60);
    
    return hoursSinceDeployment <= 48;
  } catch (err) {
    console.error('Unexpected error checking version freshness:', err);
    return false;
  }
}

/**
 * Gets the total count of changelog entries
 */
export async function getChangelogCount(): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('system_changelogs')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error('Failed to get changelog count:', error);
      return 0;
    }
    
    return count || 0;
  } catch (err) {
    console.error('Unexpected error getting changelog count:', err);
    return 0;
  }
}

/**
 * Searches changelogs by query string (searches version, title, description)
 */
export async function searchChangelogs(query: string, limit = 20): Promise<ChangelogEntry[]> {
  try {
    if (!query.trim()) {
      return getChangelogs(limit, 0);
    }
    
    const { data, error } = await supabase
      .from('system_changelogs')
      .select('*')
      .or(`version.ilike.%${query}%,title.ilike.%${query}%,description.ilike.%${query}%`)
      .order('deployed_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Failed to search changelogs:', error);
      return [];
    }
    
    return data || [];
  } catch (err) {
    console.error('Unexpected error searching changelogs:', err);
    return [];
  }
}
